import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserListParams, 
  UserListResponse,
  CreateUserResponse,
  UserValidationResponse,
  ApplicationRole
} from '@/types/user';

export class UserService {
  private endpoints = apiConfig.endpoints.userManagement;

  /**
   * Get paginated list of users with optional filters
   */
  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    try {
      const response = await apiClient.get(this.endpoints.list, { params });
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Users');
    }
  }

  /**
   * Create single user or multiple users
   */
  async createUser(userData: CreateUserRequest | CreateUserRequest[]): Promise<CreateUserResponse> {
    try {
      // Ensure userData is always an array for the API
      const userDataArray = Array.isArray(userData) ? userData : [userData];
      const response = await apiClient.post(this.endpoints.create, userDataArray);
      
      return response.data;
    } catch (error: any) {
      
      // Handle validation errors with detailed error information
      if (error.response?.data?.valid === false) {
        // This is a validation error response - throw the full response for detailed handling
        const validationError = new Error(error.response.data.message || 'Validation failed');
        (validationError as any).isValidationError = true;
        (validationError as any).validationData = error.response.data;
        throw validationError;
      }
      
      // Handle other API errors
      throw new Error(error.response?.data?.message || 'Failed to create user');
    }
  }

  /**
   * Validate user data before creation
   */
  async validateUsers(users: CreateUserRequest[]): Promise<UserValidationResponse> {
    try {
      const response = await apiClient.post(this.endpoints.validate, users);
      return response.data;
    } catch (error: any) {
      // Return the validation errors if they exist
      if (error.response?.data?.valid === false) {
        return error.response.data;
      }
      throw new Error(error.response?.data?.message || 'Failed to validate users');
    }
  }

  /**
   * Update existing user
   */
  async updateUser(userData: UpdateUserRequest): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiClient.patch(this.endpoints.update, userData);
      return response.data;
    } catch (error: any) {
      
      // Handle validation errors for updates as well
      if (error.response?.data?.valid === false) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        (validationError as any).isValidationError = true;
        (validationError as any).validationData = error.response.data;
        throw validationError;
      }
      
      throw new Error(error.response?.data?.message || 'Failed to update user');
    }
  }

  /**
   * Delete user
   */
  async deleteUser(userId: { db_id: number; fusion_auth_user_id: string }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(this.endpoints.delete, userId);
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Delete User');
    }
  }

  /**
   * Get all available application roles
   */
  async getApplicationRoles(): Promise<{ roles: (string | ApplicationRole)[] }> {
    try {
      const response = await apiClient.get(this.endpoints.roles);
      
      // Handle different possible response formats
      if (Array.isArray(response.data)) {
        return { roles: response.data };
      } else if (response.data?.roles && Array.isArray(response.data.roles)) {
        return response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        return { roles: response.data.data };
      } else {
        return { roles: [] };
      }
    } catch (error: any) {
      handleServiceError(error, 'Fetch Application Roles');
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: { db_id: number; fusion_auth_user_id: string }): Promise<User> {
    try {
      // Note: This endpoint may need to be implemented in the backend
      // For now, we'll use the list endpoint with specific filters
      const params = { 
        page: 1, 
        page_size: 1,
        q: userId.fusion_auth_user_id 
      };
      const response = await this.getUsers(params);
      
      const user = response.list.find(u => 
        u.db_id === userId.db_id && u.fusion_auth_user_id === userId.fusion_auth_user_id
      );
      
      if (!user) {
        throw new Error('User not found');
      }
      
      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch user');
    }
  }

  /**
   * Search users by query string
   */
  async searchUsers(query: string, params: Partial<UserListParams> = {}): Promise<UserListResponse> {
    try {
      const searchParams = {
        ...params,
        q: query,
        page: params.page || 1,
        page_size: params.page_size || 10
      };
      
      return await this.getUsers(searchParams);
    } catch (error: any) {
      handleServiceError(error, 'Search Users');
    }
  }
}

// Export singleton instance
export const userService = new UserService();
export default userService;